import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, Eye, EyeOff, Download, Inbox } from "lucide-react";
import { SubHeader, EmptyState } from "@/components/Brand";
import { useApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rp, fmtDate, fmtTime, txBy } from "@/lib/format";
import { AccountNo } from "@/components/AccountNo";
import { downloadMutasi } from "@/lib/pdf";

const TABS = [["mutasi", "Mutasi"], ["ringkasan", "Ringkasan"], ["simpanan", "Simpanan"]];
const TYPE_INFO = {
  pokok: ["Simpanan Pokok", "Dibayar sekali saat menjadi anggota, tidak dapat ditarik selama masih anggota."],
  wajib: ["Simpanan Wajib", "Disetor rutin setiap bulan sesuai ketentuan CU."],
  sukarela: ["Simpanan Sukarela", "Dapat disetor dan ditarik kapan saja; dipakai untuk transfer & pembayaran saldo."],
};

export default function Savings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data } = useApi("/savings");
  const [show, setShow] = useState(true);
  const [tab, setTab] = useState("mutasi");
  if (!data) return null;
  const b = data.balances;
  const txs = data.transactions;
  const totalIn = txs.filter((t) => t.direction === "in").reduce((s, t) => s + t.amount, 0);
  const totalOut = txs.filter((t) => t.direction === "out").reduce((s, t) => s + t.amount, 0);

  return (
    <div>
      <SubHeader title="Buku Tabungan" onBack={() => navigate("/app")} />
      <div className="px-5 space-y-4">
        <div className="bg-gradient-to-br from-blue-700 via-blue-600 to-sky-400 text-white rounded-2xl shadow-blue-500/30 p-5 shadow-lg fade-up" data-testid="savings-balance-card">
          <div className="flex items-center gap-4 pb-4 border-b border-white/20">
            <Wallet className="h-10 w-10" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="text-xs text-blue-100">Saldo Tersedia (Sukarela)</div>
              <div className="text-2xl font-bold font-display" data-testid="savings-available">{show ? rp(b.sukarela) : "Rp •••••••"}</div>
              <div className="text-[11px] text-emerald-100">Bisa ditransfer & ditarik</div>
              {user?.account_no && <div className="text-xs text-blue-100 mt-1 flex items-center gap-1">No. Rekening <AccountNo value={user.account_no} light className="text-white" testid="savings-account" /></div>}
            </div>
            <button onClick={() => setShow(!show)} data-testid="savings-toggle-balance" className="p-2 rounded-full hover:bg-white/10">{show ? <Eye /> : <EyeOff />}</button>
          </div>
          <div className="flex justify-between text-sm pt-2.5 font-bold">
            <span>Total Simpanan</span>
            <span data-testid="savings-total">{show ? rp(b.total) : "•••••"}</span>
          </div>
          {["pokok", "wajib", "sukarela"].map((k) => (
            <div key={k} className="flex justify-between text-sm pt-1.5 text-blue-50">
              <span className="flex items-center gap-1">{TYPE_INFO[k][0]}{k !== "sukarela" && <span className="text-[10px] bg-white/15 rounded px-1.5 py-0.5">tidak dapat ditarik</span>}</span>
              <span className="font-semibold" data-testid={`savings-${k}`}>{show ? rp(b[k]) : "•••••"}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`savings-tab-${k}`}
              className={`py-2 rounded-lg text-sm font-semibold border transition-colors ${tab === k ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>

        {tab === "mutasi" && (txs.length === 0 ? (
          <EmptyState testid="savings-empty" icon={Inbox} title="Belum ada mutasi" text="Setoran yang dicatat atau disetujui admin akan muncul di sini." />
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="savings-mutasi-table">
            <div className="grid grid-cols-[64px_1fr_80px_80px] gap-1 px-3 py-2.5 bg-slate-100 text-[11px] font-bold text-slate-700">
              <span>Tanggal</span><span>Transaksi</span><span className="text-right">Masuk</span><span className="text-right">Keluar</span>
            </div>
            {txs.map((t) => (
              <div key={t.tx_id} className="grid grid-cols-[64px_1fr_80px_80px] gap-1 px-3 py-3 border-t border-slate-100 text-xs" data-testid={`mutasi-row-${t.tx_id}`}>
                <span className="text-slate-600 leading-tight">
                  {fmtDate(t.created_at, { day: "2-digit", month: "2-digit" })}
                  <span className="block text-[10px] text-slate-500 font-mono" data-testid={`mutasi-time-${t.tx_id}`}>{fmtTime(t.created_at)}</span>
                </span>
                <span className="text-slate-800 min-w-0">
                  {t.description}
                  <span className={`mt-1 inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${t.by_type === "petugas" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`} data-testid={`mutasi-by-${t.tx_id}`}>{txBy(t)}</span>
                </span>
                <span className="text-right text-emerald-600 font-semibold">{t.direction === "in" ? rp(t.amount) : "-"}</span>
                <span className="text-right text-red-600 font-semibold">{t.direction === "out" ? rp(t.amount) : "-"}</span>
              </div>
            ))}
          </div>
        ))}

        {tab === "ringkasan" && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="savings-summary">
            <div className="flex justify-between text-sm"><span>Total masuk</span><b className="text-emerald-600">{rp(totalIn)}</b></div>
            <div className="flex justify-between text-sm"><span>Total keluar</span><b className="text-red-600">{rp(totalOut)}</b></div>
            <div className="flex justify-between text-sm"><span>Jumlah transaksi</span><b>{txs.length}</b></div>
            <div className="flex justify-between text-sm border-t pt-3"><span>Saldo akhir</span><b>{rp(b.total)}</b></div>
          </div>
        )}

        {tab === "simpanan" && (
          <div className="space-y-2" data-testid="savings-types">
            {Object.entries(TYPE_INFO).map(([k, [t, d]]) => (
              <div key={k} className="bg-white rounded-2xl border border-slate-200 p-4">
                <div className="flex justify-between"><b className="text-sm">{t}</b><b className="text-sm text-blue-800">{rp(b[k])}</b></div>
                <p className="text-xs text-slate-500 mt-1">{d}</p>
              </div>
            ))}
          </div>
        )}

        <button onClick={() => downloadMutasi(user, data)} data-testid="download-mutasi-pdf"
          className="w-full flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-800 font-semibold py-3.5 rounded-xl transition-colors">
          <Download className="h-5 w-5" /> Unduh Mutasi (PDF)
        </button>
      </div>
    </div>
  );
}
